import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CancelSchedule,
  Delete,
  Edit,
  Open,
  Schedule,
  ScheduleEdit
} from "@dreamhub/designsystem/icons";
import { SavedReport } from "@/entities/savedReports/types";
import {
  ACTIONS_TRANSLATION_KEY,
  ReportActionGroup
} from "@/components/global/Chat/components/dreamerReports/consts";

export interface ReportRowAction {
  key: string;
  /** Design separates the menu into groups; a new group draws a divider. */
  group: ReportActionGroup;
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  tooltip?: string;
  isDestructive?: boolean;
}

interface UseReportRowActionsParams {
  report: SavedReport;
  /** Opens the report, as clicking its row does. */
  onOpen: () => void;
  onStartRename: () => void;
  onSchedule: () => void;
  onRemoveSchedule: () => void;
  onDelete: () => void;
}

/**
 * The action set behind a report's three-dot menu, shared by the panel row and
 * the expanded table row so the two surfaces cannot drift.
 *
 * Open does what clicking the row does. The schedule and delete
 * actions only raise the request: their modals belong to the surface holding
 * the list, since each of those mutations can drop this very row, and a row
 * cannot outlive its own mutation.
 */
export const useReportRowActions = ({
  report,
  onOpen,
  onStartRename,
  onSchedule,
  onRemoveSchedule,
  onDelete
}: UseReportRowActionsParams) => {
  const { t } = useTranslation();

  const actions: ReportRowAction[] = useMemo(() => {
    const scheduleActions: ReportRowAction[] = report.isScheduled
      ? [
          {
            key: "editSchedule",
            group: "manage",
            label: t(`${ACTIONS_TRANSLATION_KEY}.editSchedule`),
            icon: <ScheduleEdit />,
            onSelect: onSchedule
          },
          {
            key: "removeSchedule",
            group: "manage",
            label: t(`${ACTIONS_TRANSLATION_KEY}.removeSchedule`),
            icon: <CancelSchedule />,
            onSelect: onRemoveSchedule
          }
        ]
      : [
          {
            key: "schedule",
            group: "manage",
            label: t(`${ACTIONS_TRANSLATION_KEY}.schedule`),
            icon: <Schedule />,
            onSelect: onSchedule
          }
        ];

    return [
      {
        key: "open",
        group: "primary",
        label: t(`${ACTIONS_TRANSLATION_KEY}.open`),
        icon: <Open />,
        onSelect: onOpen
      },
      {
        key: "rename",
        group: "manage",
        label: t(`${ACTIONS_TRANSLATION_KEY}.rename`),
        icon: <Edit />,
        onSelect: onStartRename
      },
      ...scheduleActions,
      {
        key: "delete",
        group: "destructive",
        label: t(`${ACTIONS_TRANSLATION_KEY}.delete`),
        icon: <Delete />,
        onSelect: onDelete,
        isDestructive: true
      }
    ];
  }, [
    t,
    report.isScheduled,
    onOpen,
    onStartRename,
    onSchedule,
    onRemoveSchedule,
    onDelete
  ]);

  return { actions };
};
